import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Ein Panel-Abonnement auf den CUI-Ereignisstrom (/ws), das sich selbst heilt.
 *
 * WARUM ES DIESEN HOOK GIBT
 * Panels, die ihre Liste einmal beim Oeffnen laden und danach nur noch von
 * WS-Ereignissen leben, sind exakt so lange korrekt wie ihre Verbindung. Reisst
 * sie ab — Ruhezustand, Netzwechsel, Server-Neustart, neu aufgebaute Webview —,
 * dann friert der Inhalt ein und sieht dabei voellig normal aus. Genau das ist
 * am 15.08.2026 in der Partner-Inbox passiert: Das Panel zeigte 403 ungelesene
 * Nachrichten, waehrend der Server 1 meldete, weil 408 Lese-Ereignisse an einer
 * toten Verbindung vorbeigelaufen waren. Das ToolHub-Badge daneben stimmte, weil
 * es alle 30s pollt.
 *
 * Ein Wiederaufbau allein reicht dagegen NICHT: Was waehrend der Auszeit
 * passiert ist, wurde nie gesendet und kommt auch nicht nach. Deshalb ist
 * `onResync` PFLICHT und kein Extra — nach jedem Wiederverbinden muss der
 * Aufrufer seinen Stand neu laden.
 *
 * KONVENTIONEN
 * Backoff und Versuchsbudget entsprechen `SessionStore` (dort liegt die robuste
 * Hauptverbindung). Zusaetzlich wird auf das globale `cui-reconnected` gehoert,
 * das SessionStore feuert, wenn der Server zurueck ist — dieselbe Quelle, die
 * u.a. QADashboard und FilePreview schon benutzen.
 */

// Bewusst identisch zu SessionStore.tsx — eine Verbindung, ein Rhythmus.
const BACKOFF_MIN_MS = 1000;
const BACKOFF_MAX_MS = 10_000;
const BACKOFF_MULTIPLIER = 2;
/** ~5 Minuten Versuche bei vollem Backoff, dann ist Aufgeben ehrlicher als stilles Weiterversuchen. */
const MAX_RECONNECT_ATTEMPTS = 30;
/** Doppel-Resync unterdruecken, wenn eigenes onopen und `cui-reconnected` fast gleichzeitig kommen. */
const RESYNC_DEDUP_MS = 1000;

export type LiveChannelState = 'connecting' | 'online' | 'reconnecting' | 'dead';

export interface LiveChannelOptions {
  /** Kurzer Name fuer Logzeilen, z.B. 'PartnerInbox'. Ohne ihn ist eine Logmeldung wertlos. */
  label: string;
  /** Bekommt jede geparste Nachricht. Fehler darin werden geloggt, nicht verschluckt. */
  onMessage: (msg: any) => void;
  /** Nach JEDEM Wiederverbinden aufzurufen: Stand neu laden, verpasste Ereignisse holen. */
  onResync: () => void;
}

export interface LiveChannel {
  state: LiveChannelState;
  /** Sofort neu versuchen — fuer einen Knopf im 'dead'-Zustand. */
  reconnectNow: () => void;
}

export function useLiveChannel({ label, onMessage, onResync }: LiveChannelOptions): LiveChannel {
  // Vertragsverletzungen sind Programmierfehler und muessen sofort auffallen,
  // nicht erst als raetselhaft eingefrorenes Panel im Betrieb.
  if (!label) {
    throw new Error('useLiveChannel: label fehlt — ohne ihn sind die Logzeilen nicht zuzuordnen.');
  }
  if (typeof onMessage !== 'function') {
    throw new Error(`useLiveChannel(${label}): onMessage muss eine Funktion sein.`);
  }
  if (typeof onResync !== 'function') {
    throw new Error(
      `useLiveChannel(${label}): onResync muss eine Funktion sein — ohne Nachladen bleibt ` +
      'nach einem Verbindungsabriss genau der Zustand stehen, der diesen Hook noetig gemacht hat.',
    );
  }

  const [state, setState] = useState<LiveChannelState>('connecting');
  const [retryNonce, setRetryNonce] = useState(0);

  // Handler leben in Refs: Panels uebergeben Inline-Funktionen, die sich bei
  // jedem Render aendern. Als Effect-Dependency wuerden sie die Verbindung
  // dauernd neu aufbauen — der Hook waere schlimmer als das Problem.
  const onMessageRef = useRef(onMessage);
  const onResyncRef = useRef(onResync);
  onMessageRef.current = onMessage;
  onResyncRef.current = onResync;

  const lastResyncRef = useRef(Number.NEGATIVE_INFINITY);
  const stateRef = useRef(state);
  stateRef.current = state;

  const resync = useCallback((reason: string) => {
    const now = Date.now();
    if (now - lastResyncRef.current < RESYNC_DEDUP_MS) return;
    lastResyncRef.current = now;
    try {
      onResyncRef.current();
    } catch (err) {
      console.warn(`[${label}] Resync nach ${reason} fehlgeschlagen:`, err);
    }
  }, [label]);

  const reconnectNow = useCallback(() => setRetryNonce(n => n + 1), []);

  useEffect(() => {
    let disposed = false;
    let ws: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let backoff = BACKOFF_MIN_MS;
    let attempts = 0;
    setState('connecting');

    const connect = () => {
      if (disposed) return;
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      // `kanal=neben`: diese Verbindung ist kein Fenster — die Fensterabfrage fragt sie nicht
      // (server/lib/guide-fenster-abfrage.ts). Sonst stand jedes Fenster als 1 + n „stumme".
      ws = new WebSocket(`${protocol}://${window.location.host}/ws?kanal=neben`);

      ws.onopen = () => {
        if (disposed) { ws?.close(); return; }
        backoff = BACKOFF_MIN_MS;
        attempts = 0;
        setState('online');
        // Initial HTTP reads can fail before the very first successful socket,
        // and manual/online recovery creates a new effect. Always reconcile.
        resync('Verbindung geöffnet');
      };

      // onerror bleibt absichtlich schmal: onclose folgt immer und ist die
      // Stelle, an der reagiert wird. Zwei Meldungen pro Abriss waeren Laerm.
      // (Frueher stand hier ein kommentarloses `() => {}` — zusammen mit einem
      // fehlenden onclose war das die eigentliche Ursache des Einfrierens.)
      ws.onerror = () => { /* siehe onclose */ };

      ws.onmessage = (e) => {
        let msg: unknown;
        try {
          msg = JSON.parse(e.data);
        } catch {
          console.warn(`[${label}] WS-Nachricht ist kein gueltiges JSON — verworfen`);
          return;
        }
        try {
          onMessageRef.current(msg);
        } catch (err) {
          // Ein Fehler in EINER Nachricht darf das Abonnement nicht kippen,
          // aber er darf auch nicht unsichtbar bleiben.
          console.warn(`[${label}] Fehler beim Verarbeiten einer WS-Nachricht:`, err);
        }
      };

      ws.onclose = () => {
        if (disposed) return;
        attempts += 1;
        if (attempts > MAX_RECONNECT_ATTEMPTS) {
          setState('dead');
          console.warn(
            `[${label}] Verbindung nach ${MAX_RECONNECT_ATTEMPTS} Versuchen aufgegeben — ` +
            'der angezeigte Stand ist ab jetzt veraltet.',
          );
          return;
        }
        setState('reconnecting');
        timer = setTimeout(connect, backoff);
        backoff = Math.min(backoff * BACKOFF_MULTIPLIER, BACKOFF_MAX_MS);
      };
    };

    // SessionStore merkt einen Server-Neustart oft frueher als dieses Panel —
    // dann ist der Stand hier schon schief, bevor die eigene Verbindung stirbt.
    const recover = () => {
      if (document.hidden) return;
      resync('Netz/Fenster wieder verfügbar');
      if (stateRef.current === 'dead' || stateRef.current === 'reconnecting') reconnectNow();
    };
    const onGlobalReconnect = recover;
    window.addEventListener('online', recover);
    document.addEventListener('visibilitychange', recover);
    window.addEventListener('cui-reconnected', onGlobalReconnect);

    connect();

    return () => {
      disposed = true;
      window.removeEventListener('cui-reconnected', onGlobalReconnect);
      window.removeEventListener('online', recover);
      document.removeEventListener('visibilitychange', recover);
      if (timer) clearTimeout(timer);
      if (ws) {
        // onclose abhaengen, sonst plant das Schliessen beim Unmount noch
        // einen Wiederaufbau ein.
        ws.onclose = null;
        ws.close();
      }
    };
  }, [label, resync, retryNonce, reconnectNow]);

  return { state, reconnectNow };
}
